import { MapPin, Star, DollarSign, Loader2 } from 'lucide-react'
import React from 'react'
import type { ToolRendererProps } from './types'

/**
 * Place search result data structure (matches Go backend PlaceSearchResult)
 */
interface PlaceSearchResult {
  description: string
  places: PlaceSummary[]
}

interface PlaceSummary {
  placeId: string
  name: string
  types: string[]
  address: string
  rating: number
  reviewCount: number
  priceLevel: number
  description: string
  distance: number
  provider: string
}

/**
 * Custom renderer for the "place" tool that displays place search results
 * in a rich card format with ratings, addresses, and pricing information.
 * Click any place card to copy full tool details to clipboard.
 */
export function PlaceToolRenderer({
  toolCall,
  toolResponse,
  isCompleted,
  copyText,
}: ToolRendererProps) {
  const handleCopy = () => {
    navigator.clipboard.writeText(copyText).catch((err) => {
      console.error('Failed to copy:', err)
    })
  }
  // Show loading state if not completed
  if (!isCompleted || !toolResponse) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-magazine-pearl rounded-lg">
        <Loader2 className="w-4 h-4 text-magazine-dust animate-spin" />
        <span className="font-sans text-caption text-magazine-ash">
          Searching for places...
        </span>
      </div>
    )
  }

  // Handle errors
  if (toolResponse.error) {
    return (
      <div className="px-3 py-2 bg-magazine-coral/10 border border-magazine-coral/20 rounded-lg">
        <p className="font-sans text-caption text-magazine-coral">
          Error: {toolResponse.error}
        </p>
      </div>
    )
  }

  // Parse the tool response content
  let result: PlaceSearchResult
  try {
    result = JSON.parse(toolResponse.content || '{}')
  } catch (error) {
    console.error('Failed to parse place tool response:', error)
    return (
      <div className="px-3 py-2 bg-magazine-coral/10 border border-magazine-coral/20 rounded-lg">
        <p className="font-sans text-caption text-magazine-coral">
          Failed to parse place results
        </p>
      </div>
    )
  }

  // Render places if we have results
  if (!result.places || result.places.length === 0) {
    return (
      <div className="px-3 py-2 bg-magazine-pearl rounded-lg">
        <p className="font-sans text-caption text-magazine-ash">
          {result.description || 'No places found'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {result.description && (
        <p className="font-sans text-caption text-magazine-ash mb-2">
          {result.description}
        </p>
      )}

      <div className="space-y-2">
        {result.places.map((place, index) => (
          <PlaceCard
            key={place.placeId || index}
            place={place}
            onCopy={handleCopy}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Individual place card component
 */
function PlaceCard({ place, onCopy }: { place: PlaceSummary; onCopy: () => void }) {
  return (
    <div
      className="bg-magazine-paper border border-magazine-pearl rounded-lg p-3 hover:border-magazine-dust transition-colors cursor-pointer"
      onClick={onCopy}
      title="Click to copy tool call details"
    >
      {/* Header with name and rating */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-sans text-body font-medium text-magazine-ink flex-1">
          {place.name}
        </h4>
        {place.rating > 0 && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <Star className="w-3.5 h-3.5 text-magazine-gold fill-magazine-gold" />
            <span className="font-sans text-caption text-magazine-ash">
              {place.rating.toFixed(1)}
            </span>
            {place.reviewCount > 0 && (
              <span className="font-sans text-caption text-magazine-dust">
                ({place.reviewCount})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Description */}
      {place.description && (
        <p className="font-sans text-caption text-magazine-ash mb-2">
          {place.description}
        </p>
      )}

      {/* Address and metadata */}
      <div className="flex items-center gap-3 text-caption">
        {place.address && (
          <div className="flex items-center gap-1 text-magazine-dust">
            <MapPin className="w-3.5 h-3.5" />
            <span className="font-sans">{place.address}</span>
          </div>
        )}

        {place.priceLevel > 0 && (
          <div className="flex items-center gap-1 text-magazine-dust">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="font-sans">
              {'$'.repeat(place.priceLevel)}
            </span>
          </div>
        )}
      </div>

      {/* Types (categories) */}
      {place.types && place.types.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {place.types.slice(0, 3).map((type, index) => (
            <span
              key={index}
              className="inline-block px-2 py-0.5 bg-magazine-pearl rounded-full font-sans text-caption text-magazine-ash"
            >
              {type.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
